import { useState, useCallback, useRef, useEffect } from 'react';
import { callAI }            from '../services/aiService.js';
import { enforceOutput, logViolations } from '../utils/outputEnforcer.js';
import { MODES, OVERRIDE_THRESHOLDS, DEFAULT_MODE } from '../constants/modeConfig.js';
import { parseResponse }     from '../utils/parser.js';
import { isEntertainmentHit } from '../utils/youtubeFilters.js';
import { TRACKS, XP_PER_LESSON_COMPLETE, XP_PER_CHECKPOINT_PASS, XP_PER_MASTERY, XP_PER_LEVEL } from '../constants/appConstants.js';
import {
  calcMasteryScore, checkMasteryGate, calcStability,
  getMasteryState, classifyError, getNextState,
} from './progressionEngine.js';
import {
  getLearnerState, upsertLearnerState, buildStatePayload,
  getLessonProgress, upsertLessonProgress, markLessonComplete,
  logInteraction, logQuizAttempt, saveMessage,
  upsertKnowledgeGap, resolveKnowledgeGap,
  upsertConceptNode, startSession, endSession,
  incrementSessionStat, updateStreak,
  getFirstLesson, getNextLesson, getLesson,
  updateLearnerProfile,
  getSessionBehaviorState, upsertSessionBehaviorState, inferBehaviorUpdates,
  getConversationSummary, upsertConversationSummary, getLastSessionSummary,
  saveNote, fetchRoadmapProgress, stampLesson1Engaged,
} from '../services/supabaseClient.js';
import { track } from '../utils/track.js';
import { logSystemEvent } from '../utils/systemEvents.js';

// While streaming, show only the prose up to the first artifact delimiter
// ([QUIZ_JSON_START], [VIDEO:…], [REFERENCE], …) so a raw artifact never flashes
// on screen. The full text is re-parsed cleanly by applyResponse on completion.
const streamSafeDisplay = (s) => {
  const i = (s || '').search(/\[[A-Z]/);
  return (i >= 0 ? s.slice(0, i) : s).trimEnd();
};

// ─────────────────────────────────────────────
// MODE RESOLVER
// Hybrid authority: reads user request + learner state → effective mode.
// Thresholds are config constants from modeConfig.js — not hardcoded here.
// Override explanations are stubs — Phase 2 UX design will finalize phrasing.
// ─────────────────────────────────────────────
function resolveMode(userMode, learnerState) {
  const mastery   = learnerState?.mastery_state?.mastery_score   || 0;
  const stability = learnerState?.mastery_state?.stability_score  || 0;
  const failures  = learnerState?.consecutive_failures             || 0;
  const t = OVERRIDE_THRESHOLDS;

  // Engine override: struggling learner → force REVIEW or LESSON
  if (failures >= t.force_review.repeated_failures || mastery < t.force_review.mastery_below) {
    if (userMode === MODES.DRILL || userMode === MODES.COMPRESS) {
      return { effectiveMode: MODES.REVIEW, overridden: true, reason: 'Switching to Review — repeated errors detected.' };
    }
  }

  // Engine override: not ready for DRILL or COMPRESS
  if ((userMode === MODES.DRILL || userMode === MODES.COMPRESS) && mastery < t.force_lesson.mastery_below) {
    return { effectiveMode: MODES.LESSON, overridden: true, reason: 'Switching to Lesson — building foundational understanding first.' };
  }

  // Honour COMPRESS request only if mastery threshold met
  if (userMode === MODES.COMPRESS && mastery >= t.allow_compress.mastery_above && stability >= t.allow_compress.stability_above) {
    return { effectiveMode: MODES.COMPRESS, overridden: false, reason: null };
  }

  // If compress requested but thresholds not met, fall to lesson
  if (userMode === MODES.COMPRESS) {
    return { effectiveMode: MODES.LESSON, overridden: true, reason: 'Switching to Lesson — compression unlocks at higher mastery.' };
  }

  // Honour user mode if no override triggered
  return { effectiveMode: userMode || DEFAULT_MODE, overridden: false, reason: null };
}

// ─────────────────────────────────────────────
// ACKNOWLEDGMENT DETECTOR
// Pure function — no side effects.
// Returns true when input is a navigation signal, not content.
// ─────────────────────────────────────────────
const ACK_WORDS = new Set([
  'ok', 'okay', 'k', 'got it', 'gotcha', 'continue', 'makes sense',
  'alright', 'sure', 'yes', 'yep', 'yup', 'understood', 'roger',
  'noted', 'move on', 'next', 'go on', 'go ahead', 'sounds good',
]);

function detectAcknowledgment(input) {
  if (!input) return false;
  const normalized = input.trim().toLowerCase().replace(/[.!,]+$/, '');
  return ACK_WORDS.has(normalized) || normalized.length <= 2;
}

// Visual-intent detection for LEARNER_QUESTION turns. A question carries visual
// intent when seeing the thing would materially help answer it — physical/spatial
// hardware, a network topology, or an explicit "show me / what does X look like"
// ask. The flag rides with the turn payload into the planner and prompt so the
// model can decide whether a short [VIDEO:] belongs on the answer. Text-only
// questions stay text-only (video is never decoration on a typed question).
const VISUAL_HARDWARE = /\b(motherboards?|ports?|slots?|connectors?|sockets?|cables?|ram sticks?|drives?|fans?|heatsinks?|chassis|racks?|switch(?:es)?|routers?|topolog(?:y|ies)|diagrams?|layouts?|circuits?)\b/i;
const VISUAL_NETWORK  = /\b(subnets?|vlans?|packet flow|osi layer diagram|star topology|mesh)\b/i;
// Intent phrases that signal wanting to SEE something, independent of subject.
const VISUAL_PHRASE   = /(what (?:does|do|is|are) .+ look like|show me|where (?:is|are)|how (?:does|do) .+ physically)/i;
// "difference between" only counts as visual when paired with a hardware term —
// on its own it is a conceptual contrast that text answers better.
const VISUAL_DIFF     = /what(?:'s| is) the difference between/i;

function detectVisualIntent(text = '') {
  const q = String(text || '');
  if (VISUAL_HARDWARE.test(q) || VISUAL_NETWORK.test(q)) return true;
  if (VISUAL_PHRASE.test(q)) return true;
  if (VISUAL_DIFF.test(q) && (VISUAL_HARDWARE.test(q) || VISUAL_NETWORK.test(q))) return true;
  return false;
}

// ─────────────────────────────────────────────
// LEARNING ENGINE HOOK
// ─────────────────────────────────────────────
export function useLearningEngine({ learner, trackId }) {
  const [loading,         setLoading]         = useState(false);
  const [display,         setDisplay]         = useState('');
  const [streamingText,   setStreamingText]   = useState(''); // live partial during a streamed turn (artifact-safe); cleared when the turn finalizes
  const [lessonData,      setLessonData]      = useState(null);
  const [sessionPhase,    setSessionPhase]    = useState('PROFILE');
  const [userMode,        setUserMode]        = useState(MODES.LESSON);
  const [effectiveMode,   setEffectiveMode]   = useState(MODES.LESSON);
  const [modeOverride,    setModeOverride]    = useState(null);
  const [behaviorState,      setBehaviorState]      = useState(null); // session_behavior_state
  const [conversationSummary, setConversationSummary] = useState(null); // rolling summary
  const [xp,              setXp]              = useState(0);
  const [level,           setLevel]           = useState(1);
  const [lastScore,       setLastScore]       = useState(null);
  const [video,           setVideo]           = useState(null);
  const [autoVideoSignal, setAutoVideoSignal] = useState(0);   // bumps on each fresh [VIDEO:] → StudyConsole auto-switches to the Video tab + autoplays
  const [reference,       setReference]       = useState(null); // Adaptive Reference panel markdown; persists across turns, cleared on lesson boundary
  // ── Learning Notes — append-only session log; rendered in the Dashboard sidebar
  const [notes,         setNotes]    = useState([]);    // [{ content, savedAt }]
  const [lastNote,      setLastNote] = useState(null);  // most recent — shown as a 3s "Saved to Learning Notes" confirmation
  // ── Break from Text — learner-triggered short video, separate from model `video`
  const [breakVideo,        setBreakVideo]        = useState(null);   // YouTube videoId once loaded
  const [breakVideoLoading, setBreakVideoLoading] = useState(false);  // search in progress
  const [breakVideoError,   setBreakVideoError]   = useState(false);  // no results / search failed
  // ── Live FOCUS — the planner-resolved concept for the CURRENT turn. Updated
  // on every applyResponse: prefer the first [CONCEPTS:] tag value emitted this
  // turn (what was actually taught), fall back to the first bolded term in the
  // rendered display. Powers BOTH the right-panel "Focus" line AND the Break
  // from Text query — keeping them in lockstep with the conversation instead of
  // the lesson-level title. Mirrored to a ref so the click-time query in
  // requestBreakVideo always reads the latest value without restaling the
  // useCallback closure.
  const [currentFocus, setCurrentFocus] = useState(null);
  const currentFocusRef = useRef(null);
  const [signals,         setSignals]         = useState({});
  const [attemptHistory,  setAttemptHistory]  = useState([]);
  const [checkpointCount, setCheckpointCount] = useState(0);
  const [quiz,            setQuiz]            = useState(null);
  const [practice,        setPractice]        = useState(null); // active PracticeCard artifact
  const [retryState,      setRetryState]      = useState({ active: false }); // fail-closed checkpoint retry UI
  const [retrievalReady,  setRetrievalReady]  = useState(false);             // model signaled retrieval beat
  const [lessonComplete,  setLessonComplete]  = useState(false); // module/track boundary — show LessonCompleteOverlay
  const [nextLesson,      setNextLesson]      = useState(null);  // { id, title } of the lesson behind Continue (null = track done)
  const [completedLesson, setCompletedLesson] = useState(null);  // { title, sequence_order } of the lesson just completed — feeds the overlay's Lesson 2/3 trial nudges
  const [remediationActive, setRemediationActive] = useState(false); // reactive mirror of inRemediationRef — drives the "Ready to Retake Quiz" control
  const [repairDelivered, setRepairDelivered] = useState(false); // ≥1 repair turn has actually rendered this arc — gates the retake control so it never appears before repair content lands
  const [recheckPassed,   setRecheckPassed]   = useState(false); // a remediation re-check just passed — show the recovery ("Continue to Next Concept") overlay
  const [paywalled,       setPaywalled]       = useState(false); // 402 trial_exhausted from /api/chat — show the paywall overlay instead of a generic error

  // The free-trial cap is enforced server-side: /api/chat returns 402
  // trial_exhausted once the learner has completed their free lessons. aiService
  // fires a global event on any blocked call (from any call site — start, send,
  // quiz, advance), so we listen once here rather than branching every catch.
  // setLoading(false) clears any in-flight spinner so the paywall is visible.
  useEffect(() => {
    const onPaywall = () => { setPaywalled(true); setLoading(false); };
    window.addEventListener('adaptos:trial_exhausted', onPaywall);
    return () => window.removeEventListener('adaptos:trial_exhausted', onPaywall);
  }, []);

  const sessionIdRef    = useRef(null);
  const historyRef      = useRef([]);         // conversation history for this session
  const stateRef        = useRef(null);       // cached learner state
  const currentLessonRef = useRef(null);      // current lesson object
  const profileDoneRef  = useRef(false);
  const practiceCorrectRef = useRef(0);       // correct practice answers this lesson
  const quizAttemptRef  = useRef(0);          // checkpoint quiz attempt # for current lesson
  const pendingKickoffRef = useRef(null);     // next-lesson title queued by model-triggered [ADVANCE]; drained by send()
  const pendingRepairRef  = useRef(false);    // fail-closed checkpoint queued a REPAIR_CHECKPOINT retry; drained by send()
  const pendingRecheckRef = useRef(false);    // remediation: force a structured re-quiz before advancement; drained by send()
  const recheckRetryRef   = useRef(0);        // consecutive REMEDIATION_RECHECK turns that produced no quiz — caps the auto-retry before a manual Try Again
  const manualRetryRef    = useRef(0);        // manual "Try Again" presses on the fail-closed checkpoint — one failed manual retry escalates to the no-deadlock continue-lesson escape
  const pendingNextLessonRef = useRef(null);  // resolved next lesson held behind the module-boundary overlay; drained by advanceToNextLesson
  const inRemediationRef  = useRef(false);    // true between a failed formal checkpoint and its re-quiz pass — lets the re-quiz render as QuizPanel even when the directive drifts to CONTINUE_LESSON
  const lesson1EngagedRef = useRef(false);    // session-local guard so engageLesson1 only does work once per mount (DB stamp is the cross-session/device dedup)

  // Whether the learner has actually been profiled/onboarded. MUST key off
  // onboarding_completed_at, NOT `experience` — `experience` has a DB default of
  // 'beginner', so it is set the instant a row is created at signup. Keying on it
  // made every brand-new user look "profiled", so they got the returning-learner
  // resume opening ("I'm back to continue… where did we leave off?") instead of a
  // first-time start — a disorienting recap that stalled the first lesson and
  // drove signup→lesson drop-off. The in-chat PROFILE flow stamps
  // onboarding_completed_at on completion (see PROFILE_COMPLETE below), so this
  // stays true across sessions once a learner is profiled either way.
  const hasProfile = !!(learner?.onboarding_completed_at);

  // ── Award XP ─────────────────────────────
  const awardXP = useCallback((amount) => {
    setXp(prev => {
      const next = prev + amount;
      setLevel(Math.floor(next / XP_PER_LEVEL) + 1);
      return next;
    });
  }, []);

  // ── First-run activation: mark Lesson 1 truly ENGAGED ──────────
  // Called from the real engagement signals (30s dwell, Continue click,
  // practice answer, checkpoint answer, completion) — NOT from a bare lesson
  // open. Gated to Lesson 1 (sequence_order === 1) so it only governs the
  // first-run card. The DB stamp is write-once and single-winner, so the
  // lesson_1_engaged analytics event fires exactly once across every trigger,
  // tab, and device; the local ref just avoids redundant requests this mount.
  const engageLesson1 = useCallback(async (trigger) => {
    if (lesson1EngagedRef.current) return;
    if (!learner?.id) return;
    const lesson = currentLessonRef.current;
    if (lesson?.sequence_order !== 1) return;   // first-run gate is Lesson 1 only
    lesson1EngagedRef.current = true;
    let firstTime = false;
    try {
      ({ firstTime } = await stampLesson1Engaged(learner.id));
    } catch { firstTime = false; }
    if (firstTime) {
      track('lesson_1_engaged', {
        trigger:   trigger || 'unknown',
        track_id:  trackId,
        lesson_id: lesson?.id || null,
        lesson_no: lesson?.sequence_order ?? null,
      });
    }
  }, [learner?.id, trackId]);

  // ── Persist learner state to Supabase ────
  const persistState = useCallback(async (updates) => {
    if (!learner?.id || !trackId) return;
    stateRef.current = { ...(stateRef.current || {}), ...updates };
    await upsertLearnerState(learner.id, trackId, updates);
  }, [learner?.id, trackId]);

  // ── Load learner state from Supabase ─────
  const loadState = useCallback(async () => {
    if (!learner?.id || !trackId) return null;
    const [state, behavior, summary] = await Promise.all([
      buildStatePayload(learner.id, trackId, currentLessonRef.current?.id),
      getSessionBehaviorState(learner.id, sessionIdRef.current),
      sessionIdRef.current
        ? getConversationSummary(learner.id, sessionIdRef.current)
        : getLastSessionSummary(learner.id),
    ]);
    stateRef.current = state;
    setBehaviorState(behavior);
    setConversationSummary(summary);
    return state;
  }, [learner?.id, trackId]);

  // ── Process AI response ───────────────────
  // sentContext carries what we actually sent to the model this turn:
  // { turnDirective, phase }. Used to guard against spurious quiz emission —
  // if we sent phase=LESSON and didn't request a checkpoint, any quiz JSON
  // the model emits is ignored.
  const applyResponse = useCallback(async (raw, sentContext = {}) => {
    const parsed = parseResponse(raw);
    const sentDirective = sentContext.turnDirective || null;
    // The server's planner is the directive arbiter — use its resolvedDirective
    // for the quiz guard and all downstream directive checks. Fall back to the
    // client's own intent only when the server didn't return a resolved value.
    const resolvedDirective = sentContext.resolvedDirective ?? sentDirective;
    const sentPhase     = sentContext.phase || sessionPhase;

    // ── Fail-closed checkpoint rendering ──────────────────────
    // A resolved CHECKPOINT_READY / REPAIR_CHECKPOINT that produced no parseable
    // quiz is a critical failure. Never render the raw assistant text — it would
    // leak quiz prose or the model's "reply 1C, 2A" fallback into chat. Suppress
    // the turn and retry once with REPAIR_CHECKPOINT (drained by send); if the
    // repair also fails, surface a calm retry state instead of raw output.
    const isCheckpointDirective = resolvedDirective === 'CHECKPOINT_READY' || resolvedDirective === 'REPAIR_CHECKPOINT' || resolvedDirective === 'REMEDIATION_RECHECK';
    // A quiz artifact was attempted (opening delimiter present) but did not parse
    // into a quiz — e.g. a malformed closing delimiter like [/QUIZ_JSON_END]. Fail
    // closed and auto-retry for a clean artifact instead of letting the turn fall
    // through to a silent [ADVANCE] that would bypass the formal QuizPanel.
    const quizArtifactLost = (raw || '').includes('[QUIZ_JSON_START]') && !parsed.quiz;
    if ((isCheckpointDirective || quizArtifactLost) && !parsed.quiz) {
      console.error('[CRITICAL] quiz artifact missing or malformed — failing closed', {
        directive:    resolvedDirective,
        artifactLost: quizArtifactLost,
        contentHead:  (raw || '').slice(0, 500),
        sessionId:    sessionIdRef.current,
      });
      // Telemetry: a quiz mechanism failure (the checkpoint couldn't render).
      // Logged to events.properties so we can monitor frequency + cause — the
      // quiz is core to the product, so this should stay near zero.
      track('quiz_failed', {
        reason:         quizArtifactLost ? 'malformed_artifact' : 'missing_artifact',
        directive:      resolvedDirective,
        lesson_id:      currentLessonRef.current?.id || null,
        lesson_title:   currentLessonRef.current?.title || null,
        lesson_no:      currentLessonRef.current?.sequence_order ?? null,
        track_id:       trackId,
        attempt:        quizAttemptRef.current,
        in_remediation: inRemediationRef.current,
        manual_retry:   manualRetryRef.current,
        content_head:   (raw || '').slice(0, 200),
        learning_session_id: sessionIdRef.current,
      });
      // Reliability log → system_events. Powers the admin Quiz Fallback Log
      // (learner · timestamp · lesson · phase). Separate from the marketing
      // events table so ops can read every fallback, including QA sessions.
      const rawStr = raw || '';
      // Decisive "why": re-extract the delimited JSON and JSON.parse it here to
      // capture the exact error message/position the tolerant parser swallows.
      // 'parsed OK …' means valid JSON but the wrong shape (no quiz.questions).
      let parse_error = null;
      if (rawStr.includes('[QUIZ_JSON_START]')) {
        const m = rawStr.match(/\[\/?QUIZ_JSON_START\]\s*([\s\S]*?)\s*\[\/?QUIZ_JSON_END\]/);
        if (!m) {
          parse_error = 'no complete delimiter block (closing delimiter missing/unmatched)';
        } else {
          try {
            const o = JSON.parse(m[1].trim());
            parse_error = `parsed OK — shape: ${o?.quiz ? `quiz.questions=${o.quiz.questions?.length ?? 'none'}` : `top keys=${Object.keys(o || {}).join(',')}`}`;
          } catch (e) { parse_error = e.message; }
        }
      }
      logSystemEvent('quiz_unavailable', {
        parse_error,
        content_tail: rawStr.slice(-400),
        lesson_id:    currentLessonRef.current?.id || null,
        lesson_title: currentLessonRef.current?.title || null,
        lesson_no:    currentLessonRef.current?.sequence_order ?? null,
        phase:        sentPhase || null,
        directive:    resolvedDirective,
        reason:       quizArtifactLost ? 'malformed_artifact' : 'missing_artifact',
        // Why it failed — the model's actual attempt + the retry context. This is
        // what lets us see whether the recheck/repair turn emitted empty JSON,
        // malformed delimiters, prose instead of a quiz, or nothing at all.
        artifact_opened: rawStr.includes('[QUIZ_JSON_START]'),
        artifact_closed: rawStr.includes('[QUIZ_JSON_END]'),
        bad_close:       rawStr.includes('[/QUIZ_JSON_END]'),   // malformed closing delimiter
        emitted_prose:   !!(parsed.display && parsed.display.trim()),
        in_remediation:  inRemediationRef.current,
        recheck_retry:   recheckRetryRef.current,
        manual_retry:    manualRetryRef.current,
        attempt:         quizAttemptRef.current,
        track_id:        trackId,
        learning_session_id: sessionIdRef.current,
        content_len:     rawStr.length,
        content_head:    rawStr.slice(0, 3000),
      });
      // No-deadlock escape: if a manual "Try Again" already fired and the
      // checkpoint STILL failed, stop retrying. Surface a calm exit that lets
      // the learner continue the lesson; the checkpoint returns naturally in a
      // later turn/session (the lesson is NOT marked complete here).
      if (manualRetryRef.current >= 1) {
        manualRetryRef.current   = 0;
        recheckRetryRef.current  = 0;
        pendingRepairRef.current = false;
        pendingRecheckRef.current = false;
        setQuiz(null);
        setRetryState({
          active: true,
          message: 'Quiz temporarily unavailable. Continue your lesson and the checkpoint will return on your next session.',
          showContinueButton: true,
        });
        return parsed;
      }
      const MAX_RECHECK_RETRIES = 2;
      if (inRemediationRef.current) {
        // Remediation re-check produced no parseable quiz. Keep the arc intact and
        // retry the SAME deterministic REMEDIATION_RECHECK request (never
        // REPAIR_CHECKPOINT / CHECKPOINT_READY — those carry the wrong contract and
        // CHECKPOINT_READY would hit the planner's practice-first gate). Cap the
        // auto-retries, then surface a manual Try Again (which re-fires the same
        // recheck path). Prose was suppressed above (returned before setDisplay), so
        // the learner is never told to answer a quiz that isn't on screen.
        if (recheckRetryRef.current < MAX_RECHECK_RETRIES) {
          recheckRetryRef.current += 1;
          setRetryState({ active: true, message: 'Rebuilding checkpoint…' });
          pendingRecheckRef.current = true;        // send() drains this → REMEDIATION_RECHECK
        } else {
          recheckRetryRef.current = 0;             // give the manual retry a fresh budget
          setRetryState({ active: true, message: 'Checkpoint unavailable. Try again in a moment.', showRetryButton: true });
        }
      } else if (resolvedDirective === 'REPAIR_CHECKPOINT') {
        // The repair retry itself produced no quiz — surface a calm manual retry (no loop).
        setRetryState({ active: true, message: 'Checkpoint unavailable. Try again in a moment.', showRetryButton: true });
      } else {
        setRetryState({ active: true, message: 'Preparing your checkpoint…' });
        pendingRepairRef.current = true;           // send() drains this → REPAIR_CHECKPOINT (clean re-emit)
      }
      return parsed; // suppress — do not render or advance state
    }
    // Any successful (non-fail-closed) turn clears a lingering retry state.
    setRetryState(prev => (prev.active ? { active: false } : prev));

    // Output enforcement — validate against effective mode schema
    const enforced = enforceOutput({ mode: effectiveMode, llmOutput: parsed.display });
    if (enforced.repaired && enforced.violations.length > 0) {
      logViolations({ mode: effectiveMode, violations: enforced.violations, sessionId: sessionIdRef.current });
    }
    setDisplay(enforced.output || parsed.display);
    // Live FOCUS for this turn — drives the Learning State "Focus" line AND the
    // Break from Text query. Priority: planner-emitted [CONCEPTS:…] first token
    // (what was actually taught this turn) > first bolded term in the rendered
    // display (the model reliably bolds the core concept). If neither yields a
    // value, leave the prior focus in place — a mid-lesson aside that mentions
    // no new concept shouldn't blank the panel.
    {
      const turnConcept = parsed.concepts?.[0] || null;
      const boldMatch   = (enforced.output || parsed.display || '').match(/\*\*([^*\n]+?)\*\*/);
      let   firstBold   = boldMatch ? boldMatch[1].trim() : null;
      // A greeting / orientation header ("Welcome back, Alex!") must not become
      // the lesson focus — otherwise the readiness panel and the gap box latch
      // onto the salutation on resume/orientation turns.
      if (firstBold && /^(welcome back|welcome|hi|hey|hello|good to see|great to see|let'?s)\b/i.test(firstBold)) firstBold = null;
      const nextFocus   = turnConcept || firstBold;
      if (nextFocus) {
        setCurrentFocus(nextFocus);
        currentFocusRef.current = nextFocus;
      }
    }
    // Video persistence: only replace when the model emits a fresh [VIDEO:] tag.
    // Responses without a tag leave the existing video in place so it stays
    // pinned across teaching turns within the same lesson. Lesson advance points
    // explicitly clear it via setVideo(null).
    if (parsed.video) {
      setVideo(parsed.video);
      // A fresh [VIDEO:] tag is the model's "this concept wants a visual" flag.
      // Signal StudyConsole to surface it — but not during a checkpoint/practice
      // turn, where yanking focus to a video would interrupt the assessment.
      if (!parsed.quiz && !parsed.practice) setAutoVideoSignal(n => n + 1);
    }
    // Reference panel persists like video — only replaced when a fresh block arrives.
    if (parsed.reference) setReference(parsed.reference);
    // Note artifact → append to Learning Notes; show a 3s "saved" confirmation.
    // Persist to learning_notes (fire-and-forget; never blocks lesson flow).
    if (parsed.noteData) {
      const entry = { content: parsed.noteData, savedAt: Date.now() };
      setNotes(prev => [...prev, entry]);
      setLastNote(parsed.noteData);
      setTimeout(() => setLastNote(curr => curr === parsed.noteData ? null : curr), 3000);
      saveNote(learner?.id, trackId, currentLessonRef.current?.id, parsed.noteData, 'auto');
    }
    setPractice(parsed.practice || null); // PracticeCard artifact (rhythm engine)
    setRetrievalReady(parsed.retrievalReady || false);
    setSignals(parsed);

    // Log interaction
    if (learner?.id && sessionIdRef.current) {
      await logInteraction({
        learner_id:    learner.id,
        session_id:    sessionIdRef.current,
        lesson_id:     currentLessonRef.current?.id || null,
        response_type: parsed.isCheckpoint ? 'quiz' : 'lesson',
        checkpoint_type: parsed.isInlineCheckpoint ? 'inline' : parsed.isEndCheckpoint ? 'end_of_lesson' : 'none',
        error_detected: parsed.errorType ? true : false,
        error_type:    parsed.errorType || 'none',
        // interaction_log.remediation_action CHECK allows lowercase
        // simplify/reframe/remediate_prerequisite/repeat/none; the model emits
        // uppercase strategies (REFRAME/REMEDIATE/...), so map before insert.
        remediation_action: ({ SIMPLIFY: 'simplify', REFRAME: 'reframe', REMEDIATE: 'remediate_prerequisite', REMEDIATE_PREREQUISITE: 'remediate_prerequisite', REPEAT: 'repeat' }[(parsed.remediationPath || '').toUpperCase()]) || 'none',
        progression_decision: parsed.advance ? 'advance' : parsed.reinforce ? 'reinforce' : parsed.rewind ? 'rewind' : 'none',
        instruction_strategy: parsed.remediationPath || null,
      });
      await incrementSessionStat(sessionIdRef.current, 'messages_count');
    }

    // ── STATE MACHINE: all transitions routed through getNextState ──
    // No direct setSessionPhase calls — events drive transitions.

    // PROFILE_COMPLETE → LESSON
    if (parsed.profileDone && !profileDoneRef.current) {
      profileDoneRef.current = true;
      setSessionPhase(s => getNextState(s, 'PROFILE_COMPLETE'));
      if (learner?.id) {
        // Stamp onboarding_completed_at so hasProfile stays true on the next
        // session — otherwise a learner profiled in-chat (rather than via the
        // PersonalizationFlow) would re-enter the PROFILE flow every visit.
        await updateLearnerProfile(learner.id, {
          experience:    'intermediate',
          learning_mode: 'balanced',
          onboarding_completed_at: new Date().toISOString(),
        });
      }
    }

    // CHECKPOINT_PRESENTED → CHECKPOINT
    if (parsed.isCheckpoint) {
      setSessionPhase(s => getNextState(s, 'CHECKPOINT_PRESENTED'));
      setCheckpointCount(c => c + 1);
      if (sessionIdRef.current) {
        await incrementSessionStat(sessionIdRef.current, 'checkpoints_attempted');
      }
    }

    // QUIZ JSON extracted → enter structured quiz mode
    // Guard: a quiz only fires when we explicitly requested a checkpoint this
    // turn (CHECKPOINT_READY, ADVANCE_LESSON_BLOCK, REMEDIATE_QUIZ_FAILURE) or
    // when we're already in a non-LESSON phase. Stops spurious quiz emission
    // immediately after a passing quiz when we hand off to the next lesson.
    if (parsed.quiz) {
      // Quiz JSON renders when the engine opened a checkpoint (CHECKPOINT_READY /
      // REMEDIATE_QUIZ_FAILURE), when we're already past LESSON phase, OR when the
      // model delivered the "## Quick Check" section. Per P-format-2 the section
      // header IS the quiz authorization: the model reaches Quick Check from the
      // lesson's section flow while the planner is often still on CONTINUE_LESSON,
      // so gating purely on the directive would suppress the end-of-lesson quiz.
      const CHECKPOINT_DIRECTIVES = ['CHECKPOINT_READY', 'REMEDIATE_QUIZ_FAILURE', 'REMEDIATION_RECHECK'];
      const checkpointRequested   = CHECKPOINT_DIRECTIVES.includes(resolvedDirective);
      const isQuickCheckSection   = /##\s*Quick Check\b/i.test(raw || '');
      // During a failed-checkpoint remediation arc, a valid quiz IS the formal
      // re-check — render it as QuizPanel even if the directive drifted to
      // CONTINUE_LESSON or the phase momentarily reads LESSON. inRemediationRef is
      // the reliable signal (set on checkpoint fail, cleared on advance), immune
      // to the React phase-state drift that otherwise suppressed the re-quiz.
      const quizAllowed           = sentPhase !== 'LESSON' || checkpointRequested || isQuickCheckSection || inRemediationRef.current;

      if (quizAllowed) {
        setQuiz(parsed.quiz);
        recheckRetryRef.current = 0;   // a valid quiz rendered — clear the re-check retry budget
        manualRetryRef.current  = 0;   // and the manual-retry escape budget
        setSessionPhase(s => getNextState(s, 'CHECKPOINT_PRESENTED'));
        setCheckpointCount(c => c + 1);
        if (sessionIdRef.current) await incrementSessionStat(sessionIdRef.current, 'checkpoints_attempted');
      } else {
        console.warn('[useLearningEngine] Suppressed unrequested quiz JSON during LESSON phase (directive:', resolvedDirective || 'none', ')');
      }
    }

    // ADVANCE signal → VALIDATION → ADVANCEMENT
    if (parsed.advance && learner?.id) {
      // TRUST GATE (mastery contract): a failed formal checkpoint resolves ONLY
      // through the structured QuizPanel re-check (handleQuizComplete), never an
      // inline [ADVANCE]. While in REMEDIATION, suppress the [ADVANCE] and force
      // the structured re-quiz instead. Without this, [ADVANCE] advances the
      // lesson AND — because the state machine has no MASTERY_CONFIRMED/
      // LESSON_ADVANCED transition out of REMEDIATION — leaves the phase stuck
      // on REMEDIATION, leaking repair state into the next lesson.
      //
      // Key off inRemediationRef (the durable arc signal), NOT sessionPhase: the
      // phase can drift REMEDIATION→CHECKPOINT mid-arc (RETRY_READY), and a phase-
      // only check would let a late [ADVANCE] slip through and advance without the
      // formal QuizPanel re-check. inRemediationRef stays true until a re-check
      // passes (cleared in performLessonAdvance), so this closes that bypass.
      if (sessionPhase === 'REMEDIATION' || inRemediationRef.current) {
        pendingRecheckRef.current = true;   // send() drains → REMEDIATION_RECHECK
        return parsed;                       // do NOT advance; do NOT clear state
      }
      setSessionPhase(s => getNextState(s, 'MASTERY_CONFIRMED'));
      awardXP(XP_PER_LESSON_COMPLETE);
      const state = stateRef.current;
      const completedModuleId = currentLessonRef.current?.module_id;
      await persistState({ last_checkpoint_result: 'pass' });
      if (currentLessonRef.current?.id) {
        // [ADVANCE] can fire on no-end-checkpoint lessons, so don't assert
        // end_checkpoint_passed here — just persist completed status + timestamp.
        await upsertLessonProgress(
          learner.id,
          currentLessonRef.current.id,
          trackId,
          currentLessonRef.current.module_id,
          { status: 'completed', mastery_level: state?.mastery_state?.level || 'understood', completed_at: new Date().toISOString() }
        );
      }
      if (sessionIdRef.current) {
        await incrementSessionStat(sessionIdRef.current, 'checkpoints_passed');
      }
      // ADVANCEMENT → next lesson. Same module: silent auto-advance. Module
      // boundary or end-of-track: raise the completion overlay instead, and let
      // the learner tap Continue to proceed (P-flow-mvp-2).
      const next = await getNextLesson(currentLessonRef.current.id);
      if (next && next.module_id === completedModuleId) {
        currentLessonRef.current = next;
        setLessonData(next);
        setVideo(null); setReference(null); setBreakVideo(null); setBreakVideoLoading(false); setBreakVideoError(false);
        setCurrentFocus(null); currentFocusRef.current = null;
        setCheckpointCount(0);
        setAttemptHistory([]);
        quizAttemptRef.current = 0;
        historyRef.current = [];
        await persistState({ current_lesson_id: next.id, last_checkpoint_result: null, last_error_type: null });
        setSessionPhase(s => getNextState(s, 'LESSON_ADVANCED'));
        // Queue a silent kickoff for the new lesson. We're inside send() right now
        // (loading is held true), so we can't re-enter send() here — send() drains
        // this ref after setLoading(false). This re-seeds historyRef with the
        // kickoff turn so the next learner input ships with non-empty
        // conversationHistory; without it isSessionStart=true → RESUME RULE fires
        // mid-session. Mirrors handleLessonAdvance's client-triggered kickoff.
        pendingKickoffRef.current = next.title;
      } else {
        // Boundary / end-of-track — defer to the overlay. Pre-advance
        // current_lesson_id so a bail-out resumes correctly; advanceToNextLesson
        // (Continue tap) drains pendingNextLessonRef and kicks off teaching.
        if (next) {
          pendingNextLessonRef.current = next;
          await persistState({ current_lesson_id: next.id, last_checkpoint_result: null, last_error_type: null });
          setNextLesson({ id: next.id, title: next.title });
        } else {
          pendingNextLessonRef.current = null;
          setNextLesson(null);
        }
        setLessonComplete(true);
      }
    }

    // ── EVAL JSON — adaptive loop closure ────────────────────────
    // checkpoint_evaluation JSON block → persist state → drive next call
    if (parsed.evalResult) {
      const {
        correct,
        errorType,
        targetConcept,
      } = parsed.evalResult;

      // Only persist columns that exist on learner_state. The remediation-context
      // fields (target_concept / last_wrong_answer / reasoning_pattern /
      // last_remediation_strategy / last_remediation_instruction) have no columns
      // here — writing them made PostgREST reject the WHOLE upsert, silently
      // dropping the valid fields (last_checkpoint_result, last_error_type) too.
      await persistState({
        last_checkpoint_result: correct ? 'pass' : 'fail',
        last_error_type:        errorType || null,
      });

      if (correct) {
        // Track practice correct count for acknowledgment routing
        practiceCorrectRef.current += 1;
        // ANSWER_CORRECT → VALIDATION
        awardXP(XP_PER_CHECKPOINT_PASS);
        setSessionPhase(s => getNextState(s, 'ANSWER_CORRECT'));
        if (sessionIdRef.current) {
          await incrementSessionStat(sessionIdRef.current, 'checkpoints_passed');
        }
      } else {
        // ANSWER_INCORRECT → REMEDIATION
        setSessionPhase(s => getNextState(s, 'ANSWER_INCORRECT'));
        if (sessionIdRef.current) {
          await incrementSessionStat(sessionIdRef.current, 'errors_detected');
          await incrementSessionStat(sessionIdRef.current, 'remediation_triggered');
        }
        if (learner?.id && (targetConcept || errorType)) {
          await upsertKnowledgeGap(
            learner.id,
            targetConcept || `Checkpoint: ${currentLessonRef.current?.title || 'unknown'}`,
            errorType || 'gap',
            'medium',
            currentLessonRef.current?.id || null,
          );
        }
      }
    }

    // REINFORCE signal → REMEDIATION
    if (parsed.reinforce) {
      setSessionPhase(s => getNextState(s, 'ANSWER_INCORRECT'));
      await persistState({ last_checkpoint_result: 'fail' });
    }

    // REWIND signal → REMEDIATION
    if (parsed.rewind) {
      setSessionPhase(s => getNextState(s, 'ANSWER_INCORRECT'));
    }

    // RETRY_READY: after REMEDIATION response, transition back to CHECKPOINT
    if (sessionPhase === 'REMEDIATION' && !parsed.evalResult && !parsed.reinforce && !parsed.rewind) {
      setSessionPhase(s => getNextState(s, 'RETRY_READY'));
    }

    // ── Post-turn conversation summary update ───────────────────
    // After assistant response, update rolling summary.
    // Runs async — does not block the UI.
    if (learner?.id && sessionIdRef.current && parsed.display) {
      const lesson = currentLessonRef.current;
      const summaryUpdates = {
        current_goal:          `${TRACKS[trackId]?.label || trackId} certification`,
        current_lesson_context: lesson ? `${lesson.module_title || ''} — ${lesson.title || ''}` : null,
        next_best_step:        parsed.advance ? 'Advance to next lesson' : 'Continue current lesson',
      };
      if (parsed.summary) {
        summaryUpdates.summary = parsed.summary;
      }
      // Accumulate concept/misconception history instead of overwriting it each
      // turn — fetch the existing row, append this turn's values, cap at last 20.
      // Concepts come from two sources: the [CONCEPTS:] tag emitted on teaching
      // turns (plain learner-facing names) and a checkpoint eval's target_concept.
      // Both feed key_concepts_covered so the RESUME RULE and the No-Concept-
      // Repetition guard have data during all-teaching sessions — not only after
      // a checkpoint, which is what left the record empty and caused re-teaching
      // on return.
      const taughtConcepts   = parsed.concepts || [];
      const hasConcept       = !!parsed.evalResult?.targetConcept;
      const hasMisconception = parsed.evalResult?.correct === false && !!parsed.evalResult?.reasoningPattern;
      if (taughtConcepts.length || hasConcept || hasMisconception) {
        const existingSummary = await getConversationSummary(learner.id, sessionIdRef.current);
        if (taughtConcepts.length || hasConcept) {
          summaryUpdates.key_concepts_covered = [...new Set([
            ...(existingSummary?.key_concepts_covered || []),
            ...taughtConcepts,
            ...(hasConcept ? [parsed.evalResult.targetConcept] : []),
          ])].slice(-20);
        }
        if (hasMisconception) {
          summaryUpdates.misconceptions_found = [
            ...(existingSummary?.misconceptions_found || []),
            parsed.evalResult.reasoningPattern,
          ].slice(-20);
        }
      }
      upsertConversationSummary(learner.id, sessionIdRef.current, summaryUpdates)
        .then(() => setConversationSummary(prev => ({ ...(prev || {}), ...summaryUpdates })));
    }

    // ── Post-turn behavior write-back ────────────────────────────
    // Infer behavioral signals from the learner's last message + eval result.
    // Writes to session_behavior_state so the NEXT call knows how to be with this learner.
    // This is the relationship memory layer — separate from mastery/learning events.
    if (learner?.id && sessionIdRef.current) {
      const behaviorUpdates = inferBehaviorUpdates(null, parsed.evalResult, sessionPhase);
      if (behaviorUpdates) {
        upsertSessionBehaviorState(learner.id, sessionIdRef.current, behaviorUpdates)
          .then(() => {
            setBehaviorState(prev => ({ ...(prev || {}), ...behaviorUpdates }));
          });
      }
    }

    // ── Repair-delivered gate ────────────────────────────────────
    // Mark repair content as landed once a remediation TEACHING turn actually
    // renders (display present, no quiz). Until this flips true, StudyConsole shows
    // "AdaptOS is repairing the gap…" rather than the retake control — so "Ready to
    // Retake Quiz" never appears before real repair content is on screen. The re-quiz
    // turn (parsed.quiz) and fail-closed turns (early-returned above) don't flip it.
    if (inRemediationRef.current && parsed.display && !parsed.quiz) {
      setRepairDelivered(true);
    }

    return parsed;
  }, [learner?.id, trackId, awardXP, persistState]);

  // ── Send a message ────────────────────────
  const send = useCallback(async (userInput, options = {}) => {
    if (!userInput?.trim() || loading) return;
    setLoading(true);
    setStreamingText('');
    // Do NOT clear video here. Video should persist across user input within
    // the same lesson and only clear on lesson advance or when the model emits
    // a fresh [VIDEO:] tag (handled in applyResponse).

    // phaseOverride lets callers (e.g. handleQuizComplete) specify the intended
    // phase so React's async state update doesn't pollute the next API call.
    const currentPhase = options.phaseOverride || sessionPhase;

    // ── Acknowledgment routing ─────────────────────────────
    // "ok", "got it", "continue" etc. are navigation signals, not content.
    // Route deterministically instead of letting the model interpret them.
    // options.turnDirective lets callers (e.g. handleQuizComplete after a sub-80%
    // quiz) pin the directive explicitly, bypassing the heuristic detection.
    let turnDirective = options.turnDirective || null;
    let visualIntent  = false;
    if (!turnDirective) {
      if (userInput === '__CONTINUE_LESSON__') {
        // "Continue Lesson" button — pure navigation signal, NOT a checkpoint
        // request. Routed here so it never falls into acknowledgment →
        // GO_TO_PRACTICE → CHECKPOINT_READY. CONTINUE_LESSON is intentionally
        // not in CHECKPOINT_DIRECTIVES, so the quiz guard in applyResponse will
        // suppress any quiz JSON the model tries to emit on this turn.
        turnDirective = 'CONTINUE_LESSON';
      } else if (detectAcknowledgment(userInput) && currentPhase === 'LESSON') {
        if (practiceCorrectRef.current >= 1) {
          // Practice done — go to checkpoint
          turnDirective = 'CHECKPOINT_READY';
        } else {
          // No practice yet — go to practice, not reteaching
          turnDirective = 'GO_TO_PRACTICE';
        }
      } else {
        // Typed learner question → answer it conversationally (LEARNER_QUESTION)
        // instead of forcing a lesson section. Reached only for genuine free-typed
        // input: the __CONTINUE_LESSON__ sentinel and acknowledgment words are
        // handled above, so detectAcknowledgment runs first and an acknowledgment
        // is never overridden into LEARNER_QUESTION.
        const q = userInput.trim();
        const isQuestion =
          q.endsWith('?') ||
          /^(what|why|how|when|where|which|who|does|can|is|are|will|should)\b/i.test(q);
        if (isQuestion) {
          turnDirective = 'LEARNER_QUESTION';
          visualIntent  = detectVisualIntent(q);
        }
      }
    }

    // Add user message to history
    const userMsg = { role: 'user', content: userInput };
    historyRef.current = [...historyRef.current, userMsg];

    // Save message to Supabase (skip when caller asks for silent — used for
    // internal sentinel messages like new-lesson kickoff after a quiz pass)
    if (!options.silent) {
      saveMessage(learner?.id, trackId, currentLessonRef.current?.id, sessionIdRef.current, 'user', userInput, currentPhase);
    }

    // Infer behavioral signals from user message (emotional signals, pace requests, etc.)
    if (learner?.id && sessionIdRef.current) {
      const userBehaviorUpdates = inferBehaviorUpdates(userInput, null, currentPhase);
      if (userBehaviorUpdates) {
        setBehaviorState(prev => ({ ...(prev || {}), ...userBehaviorUpdates }));
        upsertSessionBehaviorState(learner.id, sessionIdRef.current, userBehaviorUpdates);
      }
    }

    try {
      // Refresh state payload
      const statePayload = await loadState();

      const { content: raw, resolvedDirective } = await callAI({
        trackId,
        conversationHistory: historyRef.current.slice(0, -1),
        userInput,
        statePayload,
        lessonData:          currentLessonRef.current,
        isProfileSession:    currentPhase === 'PROFILE' && !profileDoneRef.current,
        effectiveMode,
        behaviorState,
        conversationSummary,
        sessionId:     sessionIdRef.current,
        sessionPhase:  currentPhase,
        attemptHistory,
        turnDirective,
        visualIntent,
        // Internal lesson advance (silent "Begin lesson" kickoff) ships empty history
        // but must NOT be treated as a returning session → suppresses Welcome Back.
        isInternalAdvance: options.internalAdvance || false,
        onToken: (_d, full) => setStreamingText(streamSafeDisplay(full)),
      });

      // Add assistant response to history
      historyRef.current = [...historyRef.current, { role: 'assistant', content: raw }];

      // Save to Supabase
      saveMessage(learner?.id, trackId, currentLessonRef.current?.id, sessionIdRef.current, 'assistant', raw, currentPhase);

      // Apply response — pass both client intent and the server's resolved
      // directive so the guard arbitrates on the arbiter's decision.
      await applyResponse(raw, { turnDirective, resolvedDirective, phase: currentPhase });

    } catch (err) {
      // trial_exhausted is the paywall signal, not a failure — the global
      // listener flips `paywalled`; don't paint a generic error over it.
      if (err?.code === 'trial_exhausted') setPaywalled(true);
      else {
        setDisplay('Something went wrong. Let\'s try again.');
        console.error('send error:', err);
      }
    }

    setLoading(false);

    // Model-triggered [ADVANCE] (in applyResponse above) cleared historyRef for
    // the new lesson and queued its title here. Loading is now false, so this
    // self-referential send proceeds (its closure captured loading=false) and
    // re-seeds historyRef with the kickoff turn before the learner's next input.
    // Mirrors handleLessonAdvance's silent kickoff for the client-triggered path.
    if (pendingKickoffRef.current) {
      const nextTitle = pendingKickoffRef.current;
      pendingKickoffRef.current = null;
      await send(`Begin lesson: ${nextTitle}.`, { phaseOverride: 'LESSON', silent: true, internalAdvance: true });
    }

    // Fail-closed checkpoint queued a repair retry. Loading is now false, so this
    // self-referential send proceeds. REPAIR_CHECKPOINT asks for the quiz artifact
    // only; if it still fails, applyResponse surfaces the retry state (no loop).
    if (pendingRepairRef.current) {
      pendingRepairRef.current = false;
      await send('[SYSTEM] Automated retry — emit the checkpoint quiz artifact now. This is not a learner answer; do not grade it.', { phaseOverride: 'CHECKPOINT', turnDirective: 'REPAIR_CHECKPOINT', silent: true });
    }

    // Remediation forced re-check: a [ADVANCE] arrived during REMEDIATION and was
    // suppressed above. Drain here (loading now false) → request the structured
    // targeted re-quiz. Passing it (handleQuizComplete, attempt ≥2, ≥80%) is the
    // only way out of remediation; failing it stays in the repair/re-check loop.
    if (pendingRecheckRef.current) {
      pendingRecheckRef.current = false;
      await send('[SYSTEM] Automated retry — emit the targeted re-check quiz artifact now. This is not a learner answer; do not grade it.', { phaseOverride: 'CHECKPOINT', turnDirective: 'REMEDIATION_RECHECK', silent: true });
    }
  }, [loading, sessionPhase, learner?.id, trackId, loadState, applyResponse]);

  // ── Perform a lesson advance (silent kickoff) ────────────────
  // The state-reset + silent kickoff shared by the same-module auto-advance and
  // the module-boundary overlay's "Continue" tap. Does NOT award XP or resolve
  // the next lesson — callers do that, then hand the resolved lesson here.
  const performLessonAdvance = useCallback(async (next) => {
    currentLessonRef.current = next;
    setLessonData(next);
    setVideo(null); setReference(null); setBreakVideo(null); setBreakVideoLoading(false); setBreakVideoError(false);
    setCurrentFocus(null); currentFocusRef.current = null;
    setQuiz(null);
    setAttemptHistory([]);
    practiceCorrectRef.current = 0;
    historyRef.current = [];
    quizAttemptRef.current = 0;
    pendingRecheckRef.current = false;
    recheckRetryRef.current = 0;        // clear the re-check retry budget on a clean exit
    inRemediationRef.current = false;   // remediation arc resolved — exiting to a fresh lesson
    setRemediationActive(false);        // hide the retake control on the fresh lesson
    setRepairDelivered(false);          // reset the repair-delivered gate
    setRecheckPassed(false);            // clear the recovery overlay flag
    setCompletedLesson(null);           // clear the just-completed lesson trial-nudge context
    // Clear remediation-specific state so the next lesson never inherits it:
    // stale rolling summary, and the behavior interaction-mode/error-pattern that
    // otherwise keep the right-side Learning State on "Repair" after advancement.
    setConversationSummary(null);
    if (learner?.id && sessionIdRef.current) {
      const reset = { current_interaction_mode: 'lesson_delivery', current_support_need: null, last_error_pattern: null };
      setBehaviorState(prev => ({ ...(prev || {}), ...reset }));
      upsertSessionBehaviorState(learner.id, sessionIdRef.current, reset);
    }
    await persistState({ current_lesson_id: next.id, last_checkpoint_result: null, consecutive_failures: 0 });
    setSessionPhase('LESSON');
    await send(`Begin lesson: ${next.title}.`, { phaseOverride: 'LESSON', silent: true, internalAdvance: true });
  }, [persistState, send, learner?.id]);

  // ── Advance past the module-boundary overlay (Continue tap) ──
  // current_lesson_id was already pre-advanced when the overlay was raised, so a
  // bail-out resumes at the right lesson; this just drains the held lesson and
  // kicks off teaching. Track-complete (no held lesson) clears the overlay only.
  const advanceToNextLesson = useCallback(async () => {
    const next = pendingNextLessonRef.current;
    pendingNextLessonRef.current = null;
    setLessonComplete(false);
    setNextLesson(null);
    if (next) await performLessonAdvance(next);
  }, [performLessonAdvance]);

  // ── Handle quiz answer ────────────────────
  const submitAnswer = useCallback(async (questionText, userAnswer, correctAnswer, isCorrect, responseTimeSeconds) => {
    if (!learner?.id) return;

    // Log quiz attempt
    await logQuizAttempt({
      learner_id:     learner.id,
      lesson_id:      currentLessonRef.current?.id,
      checkpoint_type: checkpointCount <= 2 ? 'inline' : 'end_of_lesson',
      question_text:  questionText,
      learner_answer: userAnswer,
      correct_answer: correctAnswer,
      is_correct:     isCorrect,
      response_time_seconds: responseTimeSeconds,
    });

    // Update attempt history for stability calc
    const newHistory = [...attemptHistory, isCorrect ? 1 : 0];
    setAttemptHistory(newHistory);

    // Recalculate mastery
    const accuracy  = newHistory.filter(Boolean).length / newHistory.length;
    const stability = calcStability(newHistory);
    const state     = stateRef.current;
    const transfer  = state?.mastery_state?.transfer_score || 0;
    const recall    = state?.mastery_state?.calibration_error != null
      ? 1 - state.mastery_state.calibration_error : 0.5;

    const masteryLevel  = getMasteryState(accuracy);
    const masteryScore  = calcMasteryScore(accuracy, stability, transfer, recall, masteryLevel === 'mastered' ? 'validation' : masteryLevel === 'understood' ? 'intermediate' : 'early');
    const canAdvance    = checkMasteryGate(accuracy, stability, transfer, masteryScore);

    setLastScore(Math.round(accuracy * 100));

    // Persist updated state
    await persistState({
      mastery_state:   masteryLevel,
      mastery_score:   masteryScore,
      stability_score: stability,
      accuracy_score:  accuracy,
      last_checkpoint_result: isCorrect ? 'pass' : 'fail',
    });

    // Update lesson progress
    if (currentLessonRef.current?.id) {
      await upsertLessonProgress(
        learner.id,
        currentLessonRef.current.id,
        trackId,
        currentLessonRef.current.module_id,
        {
          mastery_score:   masteryScore,
          stability_score: stability,
          mastery_level:   masteryLevel,
          inline_checkpoints_passed: isCorrect ? (checkpointCount + 1) : checkpointCount,
          status: canAdvance ? 'completed' : 'in_progress',
        }
      );
    }

    if (isCorrect) {
      awardXP(XP_PER_CHECKPOINT_PASS);
      if (canAdvance) awardXP(XP_PER_MASTERY);
    } else {
      // Classify error and log knowledge gap
      const errorSignals = {
        confidentAndWrong:  false, // would need confidence signal from UI
        consistentlyWrong:  !canAdvance && newHistory.filter(v => !v).length >= 2,
        nearMiss:           accuracy >= 0.6 && !canAdvance,
        overloaded:         false,
        unstable:           stability < 0.4,
      };
      const path = classifyError(errorSignals);
      await persistState({ last_error_type: path === 'REFRAME' ? 'misconception' : path === 'REMEDIATE' ? 'gap' : path === 'REPEAT' ? 'recall' : 'application' });

      // Log knowledge gap
      if (questionText) {
        await upsertKnowledgeGap(
          learner.id,
          questionText.slice(0, 100),
          path === 'REFRAME' ? 'misconception' : path === 'REMEDIATE' ? 'gap' : 'recall',
          accuracy < 0.4 ? 'high' : accuracy < 0.65 ? 'medium' : 'low',
          currentLessonRef.current?.id
        );
      }

      await incrementSessionStat(sessionIdRef.current, 'errors_detected');
      await incrementSessionStat(sessionIdRef.current, 'remediation_triggered');
    }

    // Send result to AI for response
    const resultMsg = isCorrect
      ? `I answered correctly: ${userAnswer}`
      : `I answered incorrectly. My answer was: ${userAnswer}. The correct answer was: ${correctAnswer}`;

    await send(resultMsg);
    return { canAdvance, masteryScore, accuracy };
  }, [learner?.id, trackId, checkpointCount, attemptHistory, persistState, awardXP, send]);

  // ── Advance to next lesson ────────────────
  const advanceLesson = useCallback(async () => {
    if (!currentLessonRef.current) return;
    const next = await getNextLesson(currentLessonRef.current.id);
    if (!next) {
      setDisplay('You have completed all lessons in this track. Outstanding work.');
      return;
    }
    currentLessonRef.current = next;
    setLessonData(next);
    setVideo(null); setReference(null); setBreakVideo(null); setBreakVideoLoading(false); setBreakVideoError(false);
    setCurrentFocus(null); currentFocusRef.current = null;
    setAttemptHistory([]);
    setCheckpointCount(0);
    quizAttemptRef.current = 0;
    setSessionPhase('LESSON');
    historyRef.current = [];
    await persistState({
      current_lesson_id: next.id,
      last_checkpoint_result: null,
      last_error_type: null,
    });
    await send(`Begin lesson: ${next.title}`, { internalAdvance: true });
  }, [persistState, send]);

  // ── Start a mission (entry point) ─────────
  const startMission = useCallback(async (overrideLessonId) => {
    if (!learner?.id) return;
    setLoading(true);
    setDisplay('');
    setStreamingText('');
    setVideo(null); setReference(null); setBreakVideo(null); setBreakVideoLoading(false); setBreakVideoError(false);
    setCurrentFocus(null); currentFocusRef.current = null;
    historyRef.current = [];
    setAttemptHistory([]);
    setCheckpointCount(0);
    quizAttemptRef.current = 0;
    profileDoneRef.current = hasProfile;

    // Start Supabase session
    const sessionId = await startSession(learner.id, trackId);
    sessionIdRef.current = sessionId;

    // Update streak
    updateStreak(learner.id);

    // Safety net — persist current_track in case the App-level write missed.
    // updateLearnerProfile is a no-op on a learners row that already has the same value.
    if (trackId) {
      updateLearnerProfile(learner.id, { current_track: trackId });
    }

    // Determine starting lesson
    let lesson = null;
    if (overrideLessonId) {
      lesson = await getLesson(overrideLessonId);
    } else {
      // buildStatePayload doesn't expose current_lesson_id — read raw state directly
      const [existingState, rawState] = await Promise.all([
        loadState(),
        getLearnerState(learner.id, trackId),
      ]);
      const resumeLessonId = rawState?.current_lesson_id || existingState?.current_lesson_id;
      if (resumeLessonId) {
        lesson = await getLesson(resumeLessonId);
      }
      if (!lesson) {
        lesson = await getFirstLesson(trackId);
      }
    }

    currentLessonRef.current = lesson;
    setLessonData(lesson);

    // Set initial phase
    // Initial state — PROFILE for new users, LESSON for returning
    setSessionPhase(hasProfile ? 'LESSON' : 'PROFILE');

    // Resume detection — read lesson_progress BEFORE the in_progress upsert
    // below overwrites it. A pre-existing in_progress/completed row means the
    // learner is genuinely resuming this lesson (vs. opening it fresh this
    // session). Without capturing it here, the upsert makes every lesson read
    // 'in_progress' from turn 1, which would suppress orientation even on a
    // brand-new lesson. Drives the RESUME RULE no-summary fallback.
    const priorProgress = (lesson && learner.id) ? await getLessonProgress(learner.id, lesson.id) : null;
    const isResumingLesson = priorProgress?.status === 'in_progress' || priorProgress?.status === 'completed';

    // Lesson 1 OPENED — fire only when the learner is actually starting lesson 1
    // fresh (not resuming). priorProgress is null on a brand-new run. NOTE:
    // "opened" is NOT activation (Clarity: 3–4s opens that bounce). True
    // engagement fires separately via engageLesson1 (30s dwell / Continue /
    // practice / checkpoint / completion). `first_lesson_started` is kept as a
    // preserved alias — the Outreach "Activation Speed" card still queries it.
    if (lesson?.sequence_order === 1 && !isResumingLesson) {
      const openProps = { lesson_id: lesson.id, track_id: trackId, lesson_no: lesson.sequence_order ?? null };
      track('first_lesson_started', { lesson_id: lesson.id }); // alias — do not remove (Activation Speed query)
      track('lesson_1_opened', openProps);
    }

    // Persist starting lesson
    if (lesson) {
      await persistState({ current_lesson_id: lesson.id });
      if (learner.id) {
        await upsertLessonProgress(learner.id, lesson.id, trackId, lesson.module_id, {
          // Never downgrade a completed lesson back to in_progress on reopen
          // (e.g. revisiting the final lesson of a finished track).
          status: priorProgress?.status === 'completed' ? 'completed' : 'in_progress',
          // Preserve the original start time on resume; only stamp it the first time.
          ...(priorProgress ? {} : { started_at: new Date().toISOString() }),
        });
      }
    }

    // Initial AI call
    try {
      const statePayload = await loadState();

      // No "welcome back" until the learner has been around a while. A brand-new
      // learner who returns within their first hours (or re-enters the same
      // session) should still be greeted as new — never "where did we leave off."
      // treatAsResume gates ALL resume framing (the directive, the opening line,
      // the bridge summary, and the RESUME RULE's lesson_progress signal).
      const FOUR_HOURS = 4 * 60 * 60 * 1000;
      const recentSignup = learner?.created_at
        ? (Date.now() - Date.parse(learner.created_at)) < FOUR_HOURS
        : false;
      const treatAsResume = hasProfile && isResumingLesson && !recentSignup;

      // Opening-turn resume signal — the in_progress upsert above makes
      // buildStatePayload always read 'in_progress', so override it with the
      // pre-session truth. This is the only turn the RESUME RULE consults
      // lesson_progress (it is gated on isSessionStart), so a local override is
      // sufficient and keeps a brand-new lesson from being treated as a resume.
      if (statePayload) {
        statePayload.lesson_progress = {
          status: treatAsResume ? 'in_progress' : 'not_started',
          inline_checkpoints_passed: priorProgress?.inline_checkpoints_passed ?? 0,
        };
      }

      // Opening-turn directive:
      //  - not profiled                → null (PROFILE phase runs its own protocol).
      //  - genuine resume, passive     → RESUME_RECALL (active-recall on return).
      //  - genuine resume, post-fail   → null (remediation/advancement flow owns it).
      //  - profiled + fresh start      → OPENING_BEAT (short, warm opener — no recap).
      let openingTurnDirective = null;
      if (hasProfile) {
        if (treatAsResume) {
          openingTurnDirective =
            statePayload?.last_checkpoint_result !== 'fail' && !statePayload?.consecutive_failures
              ? 'RESUME_RECALL'
              : null;
        } else {
          openingTurnDirective = 'OPENING_BEAT';
        }
      }

      // Return-session bridge for the welcome-back — only on a genuine resume.
      // Skipped for fresh starts / recent signups so a new learner is never
      // handed a "here's where we left off" summary.
      const bridgeSummary = treatAsResume ? await getLastSessionSummary(learner.id) : null;
      if (bridgeSummary) setConversationSummary(bridgeSummary);

      // Opening turn, by learner state:
      //  - not profiled        → first-time start (conversational profiling).
      //  - genuine resume       → "where did we leave off" recap.
      //  - fresh / recent signup → "let's begin" (paired with OPENING_BEAT so the
      //    lesson opens short and warm instead of stalling on a recap).
      const trackLabel = trackId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const openingInput = !hasProfile
        ? `I want to get my ${trackLabel} certification. I'm not sure where to start.`
        : treatAsResume
          ? `I'm back to continue my ${trackLabel} preparation. Where did we leave off?`
          : `I'm ready to start this lesson. Let's begin.`;

      const userMsg = { role: 'user', content: openingInput };
      historyRef.current = [userMsg];

      const { content: raw, resolvedDirective } = await callAI({
        trackId,
        conversationHistory: [],
        userInput:           openingInput,
        statePayload,
        lessonData:          lesson,
        isProfileSession:    !hasProfile,
        effectiveMode,
        behaviorState,
        conversationSummary: bridgeSummary,
        sessionId:           sessionId,
        sessionPhase:        !hasProfile ? 'PROFILE' : 'LESSON',
        attemptHistory:      [],
        turnDirective:       openingTurnDirective,
        onToken: (_d, full) => setStreamingText(streamSafeDisplay(full)),
      });

      const initPhase = !hasProfile ? 'PROFILE' : 'LESSON';
      historyRef.current = [userMsg, { role: 'assistant', content: raw }];
      saveMessage(learner.id, trackId, lesson?.id, sessionId, 'user',      openingInput, initPhase);
      saveMessage(learner.id, trackId, lesson?.id, sessionId, 'assistant', raw,          initPhase);

      // Pass the same sentContext shape the rest of the codebase relies on so
      // the spurious-quiz guard is gated on what we actually sent, not the
      // stale sessionPhase closure value at hook-init time.
      await applyResponse(raw, { turnDirective: openingTurnDirective, resolvedDirective, phase: initPhase });
    } catch (err) {
      // trial_exhausted is the paywall signal, not a failure — the global
      // listener flips `paywalled`; don't paint a generic error over it.
      if (err?.code === 'trial_exhausted') setPaywalled(true);
      else {
        setDisplay('Something went wrong. Let\'s try again.');
        console.error('startMission error:', err);
      }
    }

    setLoading(false);
  }, [learner?.id, trackId, hasProfile, loadState, persistState, applyResponse]);

  // ── Handle structured QuizPanel completion ───────────────
  // Hard 80% pass threshold. Sub-threshold quizzes route to REMEDIATION with
  // the REMEDIATE_QUIZ_FAILURE directive (re-teach failed concepts, surface a
  // video, offer options, follow with a 3-question targeted re-quiz). Attempts
  // ≥ 2 also flag failed concepts as persistent gaps. Per-attempt summary row
  // (score, failed_concepts[], attempt_number) is logged for attempt-1 vs
  // attempt-2 delta analytics.
  const PASS_THRESHOLD = 0.80;
  const handleQuizComplete = useCallback(async (correctCount, total, results = []) => {
    if (!learner?.id) return;

    engageLesson1('checkpoint');          // first-run activation: answering a checkpoint counts

    quizAttemptRef.current += 1;
    const attemptNumber = quizAttemptRef.current;
    const accuracy      = total > 0 ? correctCount / total : 0;
    const passed        = accuracy >= PASS_THRESHOLD;
    setQuiz(null);

    // Funnel instrumentation — pre-scoring + score outcome. Score is 0-100 so
    // dashboards don't have to remember the 0-1 quiz scale.
    const currentLessonId = currentLessonRef.current?.id;
    const score = Math.round(accuracy * 100);
    track('checkpoint_attempted', { lesson_id: currentLessonId });
    if (attemptNumber >= 2) track('recheck_attempted', { lesson_id: currentLessonId });
    if (passed) track('checkpoint_passed', { lesson_id: currentLessonId, score });
    else        track('checkpoint_failed', { lesson_id: currentLessonId, score });

    // Derive per-attempt summary from per-question results
    const failedItems    = results.filter(r => !r.correct);
    const failedConcepts = failedItems.map(r => r.concept).filter(Boolean);

    // ─── Per-attempt summary log (companion to per-question rows) ───
    await logQuizAttempt({
      learner_id:           learner.id,
      lesson_id:            currentLessonRef.current?.id || null,
      attempt_number:       attemptNumber,
      score:                accuracy,
      failed_concepts:      failedConcepts,
      remediation_strategy: passed ? null : 'REFRAME',
      video_triggered:      !passed,
      created_at:           new Date().toISOString(),
    });

    // Mirror per-question results into attempt history for stability calc
    const quizResultBools = results.length
      ? results.map(r => (r.correct ? 1 : 0))
      : Array.from({ length: total }, (_, i) => (i < correctCount ? 1 : 0));
    const newHistory = [...attemptHistory, ...quizResultBools];
    setAttemptHistory(newHistory);

    // Derive scores — transfer measured via quiz performance
    const stability    = calcStability(newHistory);
    const transfer     = accuracy >= 0.80 ? 0.85 : accuracy >= 0.65 ? 0.70 : 0.50;
    const recall       = 0.5;
    const masteryLevel = getMasteryState(accuracy);
    const weightStage  = masteryLevel === 'mastered' ? 'validation' : masteryLevel === 'understood' ? 'intermediate' : 'early';
    const masteryScore = calcMasteryScore(accuracy, stability, transfer, recall, weightStage);

    setLastScore(Math.round(accuracy * 100));

    await persistState({
      accuracy_score:         accuracy,
      stability_score:        stability,
      transfer_score:         transfer,
      mastery_score:          masteryScore,
      mastery_state:          masteryLevel,
      last_checkpoint_result: passed ? 'pass' : 'fail',
      consecutive_failures:   passed ? 0 : (stateRef.current?.consecutive_failures || 0) + 1,
    });

    if (currentLessonRef.current?.id) {
      if (passed) {
        // End-of-lesson checkpoint passed — persist FULL completion state:
        // status + completed_at + end_checkpoint_passed/score (accuracy is the
        // raw 0-1 quiz score; mastery_score stays the composite via extra).
        await markLessonComplete(
          learner.id, currentLessonRef.current.id, trackId, currentLessonRef.current.module_id, accuracy,
          { mastery_score: masteryScore, stability_score: stability, mastery_level: masteryLevel },
        );
      } else {
        await upsertLessonProgress(learner.id, currentLessonRef.current.id, trackId, currentLessonRef.current.module_id, {
          mastery_score:   masteryScore,
          stability_score: stability,
          mastery_level:   masteryLevel,
          status:          'in_progress',
        });
      }
    }

    // Local helper — advances to the next lesson with the defensive state resets
    // the gate-open branch has always done. Extracted so the re-quiz-pass
    // short-circuit can reuse it without duplicating the cleanup.
    const handleLessonAdvance = async ({ recovery = false } = {}) => {
      const justCompletedSeq = currentLessonRef.current?.sequence_order;
      const justCompletedId  = currentLessonRef.current?.id;
      track('lesson_advanced', { lesson_id: justCompletedId });
      if (justCompletedSeq === 1) {
        engageLesson1('lesson_complete');   // completing Lesson 1 is unambiguous engagement
        track('first_lesson_completed', { lesson_id: justCompletedId });
      }
      // first_3_lessons_completed — query roadmap; fire-and-forget. Single
      // round-trip per advance, only when the just-completed lesson is the
      // third overall (cheap probe, exact match on completed_count).
      if (trackId) {
        fetchRoadmapProgress(learner.id, trackId).then(rp => {
          if (rp?.completed_count === 3) track('first_3_lessons_completed');
        }).catch(() => {});
      }
      awardXP(XP_PER_LESSON_COMPLETE);
      const completedModuleId = currentLessonRef.current?.module_id;
      let next;
      try {
        next = await getNextLesson(currentLessonRef.current?.id);
      } catch (err) {
        // Query error (not a genuine end-of-track) — never claim completion.
        console.error('getNextLesson failed:', err);
        setDisplay('Something went wrong loading the next lesson. Let\'s try that again in a moment.');
        return;
      }
      // A remediation re-check pass is an earned recovery — ALWAYS surface the
      // post-pass overlay (even within a module) so the learner gets a clear
      // "Continue to Next Concept" beat rather than silently sliding forward.
      if (recovery) setRecheckPassed(true);
      // Module-boundary overlay (P-flow-mvp-2): advance silently WITHIN a module;
      // raise the completion overlay when crossing into a new module or finishing
      // the track. performLessonAdvance applies the same defensive state clears.
      // Capture the just-completed lesson for the overlay's trial nudges. Set
      // for both the silent same-module path AND the overlay path so the engine
      // state stays consistent (the silent path will reset it immediately on
      // performLessonAdvance).
      const justCompleted = currentLessonRef.current;
      setCompletedLesson(justCompleted ? { title: justCompleted.title, sequence_order: justCompleted.sequence_order } : null);
      if (next && next.module_id === completedModuleId && !recovery) {
        await performLessonAdvance(next);            // same module — silent
      } else {
        if (next) {
          // Pre-advance current_lesson_id so a bail-out at the overlay resumes at
          // the next lesson; performLessonAdvance re-sets it on Continue.
          pendingNextLessonRef.current = next;
          await persistState({ current_lesson_id: next.id, last_checkpoint_result: null, consecutive_failures: 0 });
          setNextLesson({ id: next.id, title: next.title });
        } else {
          pendingNextLessonRef.current = null;
          setNextLesson(null);
        }
        setLessonComplete(true);
      }
    };

    // ─── FAIL: 80% gate not met → remediation cycle, do NOT advance ───
    if (!passed) {
      // Enter the remediation arc: from here, a valid quiz artifact is the formal
      // re-check and must surface as QuizPanel regardless of the resolved directive.
      // remediationActive is the reactive mirror StudyConsole reads to expose the
      // deterministic "Ready to Retake Quiz" control once repair content lands.
      inRemediationRef.current = true;
      setRemediationActive(true);
      track('remediation_started', { lesson_id: currentLessonId });
      // Telemetry: a learner score-fail (below the 80% mastery gate). Same
      // quiz_failed event as the mechanism failure, distinguished by `reason`,
      // so we can watch both the bug rate and whether quizzes are too hard.
      track('quiz_failed', {
        reason:      'score_below_gate',
        lesson_id:   currentLessonId,
        lesson_no:   currentLessonRef.current?.sequence_order ?? null,
        track_id:    trackId,
        attempt:     attemptNumber,
        accuracy:    Math.round(accuracy * 100),
        stability:   Math.round(stability * 100),
        transfer:    Math.round(transfer * 100),
        mastery:     Math.round(masteryScore * 100),
        failed_concepts: failedConcepts,
        learning_session_id: sessionIdRef.current,
      });
      setRepairDelivered(false);   // re-gate: the retake control stays hidden until the first repair turn renders
      setRecheckPassed(false);
      recheckRetryRef.current = 0;  // fresh re-check retry budget for this arc
      if (sessionIdRef.current) {
        await incrementSessionStat(sessionIdRef.current, 'checkpoints_failed');
      }

      // Attempts ≥ 2: each still-failed concept is a persistent gap
      if (attemptNumber >= 2 && failedConcepts.length) {
        for (const concept of failedConcepts) {
          await upsertKnowledgeGap(
            learner.id,
            concept,
            'persistent_gap',
            'high',
            currentLessonRef.current?.id || null,
          );
        }
      }

      setSessionPhase(getNextState('CHECKPOINT', 'ANSWER_INCORRECT'));               // → REMEDIATION
      // Pass failed question texts to the model so REMEDIATE_QUIZ_FAILURE
      // names the specific gap instead of asking the learner to report it.
      const failedMsg = failedConcepts.length
        ? `Quiz complete. I scored ${Math.round(accuracy * 100)}%. I got these wrong: ${failedConcepts.join(' | ')}`
        : `Quiz complete. I scored ${Math.round(accuracy * 100)}%. Score below threshold — identify and repair the gap.`;
      await send(failedMsg, {
        phaseOverride:  'REMEDIATION',
        turnDirective:  'REMEDIATE_QUIZ_FAILURE',
      });
      return; // do not advance
    }

    // ─── PASS: 80%+ → advance per existing mastery-gate logic ───
    quizAttemptRef.current = 0;
    awardXP(XP_PER_CHECKPOINT_PASS);
    if (sessionIdRef.current) await incrementSessionStat(sessionIdRef.current, 'checkpoints_passed');

    // Re-quiz pass after remediation — skip the mastery gate and advance.
    // attemptHistory is polluted by the failed first attempt so checkMasteryGate
    // routes to the gate-closed branch, which doesn't reset practiceCorrectRef /
    // historyRef — the next ack then fires CHECKPOINT_READY and produces a
    // spurious second quiz on the same lesson. The REMEDIATE_QUIZ_FAILURE
    // contract ("80%+ on re-quiz, no exceptions") is the only gate that applies here.
    if (attemptNumber >= 2) {
      await handleLessonAdvance({ recovery: true });
      return;
    }

    const gateOpen = checkMasteryGate(accuracy, stability, transfer, masteryScore);

    // Calculate full phase sequence explicitly to avoid React async batching races
    const postQuizPhase    = getNextState('CHECKPOINT', 'ANSWER_CORRECT');           // → VALIDATION
    const resolutionPhase  = gateOpen
      ? getNextState(postQuizPhase, 'MASTERY_CONFIRMED')                             // → ADVANCEMENT
      : getNextState(postQuizPhase, 'MASTERY_INSUFFICIENT');                         // → LESSON
    setSessionPhase(resolutionPhase);

    if (gateOpen) {
      await handleLessonAdvance();
    } else {
      await send('Quiz complete. Good progress — let\'s reinforce a couple of areas before we advance.', { phaseOverride: 'LESSON' });
    }
  }, [learner?.id, trackId, attemptHistory, persistState, awardXP, send, engageLesson1]);

  // ── End session cleanup ───────────────────
  const endCurrentSession = useCallback(async () => {
    if (!sessionIdRef.current) return;
    await endSession(sessionIdRef.current, {
      ended_at: new Date().toISOString(),
    });
    sessionIdRef.current = null;
  }, []);

  // ── Request a mode change (UI → engine) ──────────────────────
  const requestMode = useCallback((mode) => {
    const resolution = resolveMode(mode, stateRef.current);
    setUserMode(mode);
    setEffectiveMode(resolution.effectiveMode);
    setModeOverride(resolution.overridden ? resolution.reason : null);
  }, []);

  // PracticeCard completion — clear the card and continue the lesson. The
  // rhythm reset is driven by the PRACTICE_REQUIRED trace from the card's
  // presentation turn, so this just advances with a clean (silent) message.
  // (Per-answer analytics — experience_data.practice_result — deferred: the
  // rhythm doesn't depend on it.)
  const handlePracticeComplete = useCallback(async (result) => {
    engageLesson1('practice');            // first-run activation: a PracticeCard interaction counts
    setPractice(null);
    const msg = result?.correct
      ? 'I answered the practice question correctly.'
      : 'I answered the practice question, but I think I missed it.';
    await send(msg, { phaseOverride: 'LESSON', silent: true });
  }, [send, engageLesson1]);

  // "Ready to Retake Quiz" — the deterministic remediation exit. Sends the
  // REMEDIATION_RECHECK directive (NOT generic CONTINUE_LESSON), which is a hard
  // override that forces the structured re-quiz as a QuizPanel artifact. quizAllowed
  // honors inRemediationRef so it renders; the fail-closed guard handles a missing
  // artifact (suppress prose + retry the same recheck). Passing it (handleQuizComplete,
  // attempt ≥2, ≥80%) is the ONLY way out of remediation; inline chat answers never
  // count as the formal pass. Also the single source for the Try Again retry below.
  const requestRecheck = useCallback(() => {
    if (loading) return undefined;
    setRetryState({ active: false });
    return send('[SYSTEM] Automated retry — emit the targeted re-check quiz artifact now. This is not a learner answer; do not grade it.', { phaseOverride: 'CHECKPOINT', turnDirective: 'REMEDIATION_RECHECK', silent: true });
  }, [send, loading]);

  // "Try Again" from the fail-closed retry UI. During the remediation arc it must
  // fire the SAME deterministic REMEDIATION_RECHECK request as "Ready to Retake Quiz"
  // (never CHECKPOINT_READY / CONTINUE_LESSON). Outside remediation it re-requests a
  // normal checkpoint.
  const retryCheckpoint = useCallback(() => {
    manualRetryRef.current += 1;       // a failed manual retry escalates to the continue-lesson escape
    // Clean context: drop the failed assistant turn (broken/garbage quiz
    // artifact) so the model isn't re-fed its own bad output and biased toward
    // repeating it. The fresh call then re-resolves the directive server-side.
    const hist = historyRef.current;
    if (hist.length && hist[hist.length - 1]?.role === 'assistant'
        && (hist[hist.length - 1].content || '').includes('[QUIZ_JSON_START]')) {
      historyRef.current = hist.slice(0, -1);
    }
    setQuiz(null);                     // clear any stale quiz state before the retry
    if (inRemediationRef.current) {
      recheckRetryRef.current = 0;     // fresh auto-retry budget for the manual retry
      return requestRecheck();         // identical deterministic recheck path as the button
    }
    setRetryState({ active: false });
    return send('Let\'s do the checkpoint.', { phaseOverride: 'CHECKPOINT', turnDirective: 'CHECKPOINT_READY', silent: true });
  }, [send, requestRecheck]);

  // "Continue Lesson" from the no-deadlock escape state. Abandons the broken
  // checkpoint, clears ALL checkpoint/remediation state, returns to normal
  // lesson teaching via a fresh CONTINUE_LESSON turn (which can't re-trigger the
  // checkpoint fail-closed path). The lesson is intentionally NOT marked
  // complete — the checkpoint will re-surface in a later turn/session.
  const continueAfterCheckpointFail = useCallback(() => {
    manualRetryRef.current    = 0;
    recheckRetryRef.current   = 0;
    pendingRepairRef.current  = false;
    pendingRecheckRef.current = false;
    inRemediationRef.current  = false;
    setRemediationActive(false);
    setQuiz(null);
    setRetryState({ active: false });
    setSessionPhase('LESSON');
    return send('__CONTINUE_LESSON__', { phaseOverride: 'LESSON', silent: true });
  }, [send]);

  // ── Break from Text — learner-triggered YouTube search, separate from the
  // model-triggered `video` path. Query is built FRESH AT CLICK TIME from
  // currentFocusRef — the planner-resolved concept for the most recent turn —
  // so the returned video follows the live conversation instead of repeating
  // the same gap/title across turns. Priority: currentFocus (live per-turn
  // signal: [CONCEPTS:] first token or first bolded term) > current lesson
  // title > track name; suffixed with "explained". Filters returned search
  // results to videos ≤6 min via contentDetails. No autoplay (the iframe loads
  // the player; learner clicks play). Degrades cleanly to breakVideoError on
  // any failure — no raw API errors reach the learner.
  const requestBreakVideo = useCallback(async () => {
    if (!learner?.id) { setBreakVideoError(true); return; }
    const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY;
    if (!apiKey) { setBreakVideoError(true); return; }
    setBreakVideoLoading(true);
    setBreakVideoError(false);
    setBreakVideo(null);
    try {
      // Read the live FOCUS via ref so the closure never reads a stale state
      // value — every click reflects the latest applyResponse pass. Tightening:
      // keep the full track label ("CompTIA A+", "Microsoft AZ-104") in the
      // query so cert context disambiguates noisy keywords (e.g. "transformers"
      // pulling the cartoon). Result titles + channels go through the shared
      // entertainment denylist before duration filtering.
      const focusNow    = currentFocusRef.current ? String(currentFocusRef.current).replace(/_/g, ' ').trim() : '';
      const lessonTitle = currentLessonRef.current?.title || '';
      const trackLabel  = (TRACKS[trackId]?.label || trackId || 'IT').trim();
      const base  = focusNow || lessonTitle || trackLabel;
      const query = `${base} ${trackLabel} explained`;

      const searchUrl = `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(query)}&type=video&videoDuration=medium&videoEmbeddable=true&relevanceLanguage=en&maxResults=15&key=${apiKey}`;
      const sr = await fetch(searchUrl).then(r => r.json());
      // Keep snippet metadata by id so the eligible video's title can be
      // surfaced in the "Short Video Support · 4:32 · <title>" subline.
      const snippetById = new Map();
      (sr?.items || []).forEach(it => {
        const vid     = it.id?.videoId;
        const title   = it.snippet?.title || '';
        const channel = it.snippet?.channelTitle || '';
        if (!vid) return;
        if (isEntertainmentHit({ title, channel })) return;
        snippetById.set(vid, { title, channel });
      });
      const ids = [...snippetById.keys()];
      if (!ids.length) { setBreakVideoError(true); setBreakVideoLoading(false); return; }

      const detailsUrl = `https://www.googleapis.com/youtube/v3/videos?id=${ids.join(',')}&part=contentDetails&key=${apiKey}`;
      const dr = await fetch(detailsUrl).then(r => r.json());
      const parseDur = (iso) => {
        const m = (iso || '').match(/PT(?:(\d+)M)?(?:(\d+)S)?/);
        return (parseInt(m?.[1] || '0', 10) * 60) + parseInt(m?.[2] || '0', 10);
      };
      // Preserve search-relevance ranking — iterate ids in order, pick the
      // first one inside the ≤6 min cap, rather than walking dr.items (which
      // can re-order).
      const detailsById = new Map((dr?.items || []).map(it => [it.id, it]));
      const eligibleId = ids.find(id => {
        const sec = parseDur(detailsById.get(id)?.contentDetails?.duration || '');
        return sec > 0 && sec <= 360;
      });
      if (!eligibleId) { setBreakVideoError(true); setBreakVideoLoading(false); return; }
      const eligibleSec = parseDur(detailsById.get(eligibleId).contentDetails?.duration || '');
      const fmtDur = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
      // breakVideo is now an object — StudyConsole reads .id / .title / .duration.
      setBreakVideo({
        id:       eligibleId,
        title:    snippetById.get(eligibleId)?.title || '',
        duration: fmtDur(eligibleSec),
      });
      setBreakVideoLoading(false);
    } catch (err) {
      console.warn('requestBreakVideo:', err?.message || err);
      setBreakVideoError(true);
      setBreakVideoLoading(false);
    }
  }, [learner?.id, trackId]);

  const clearBreakVideo = useCallback(() => {
    setBreakVideo(null);
    setBreakVideoLoading(false);
    setBreakVideoError(false);
  }, []);

  // ── Single adaptive primary action (StudyConsole renders exactly one) ──
  // A surface artifact (quiz / practice / completion overlay / fail-closed retry)
  // owns its own surface and button → primaryAction is null and the unified button
  // is suppressed. Otherwise one action: a remediation re-check (once repair landed
  // and idle) or its "repairing" status; a retrieval check; else continue the lesson.
  const primaryAction =
    (quiz || practice || lessonComplete || retryState?.active) ? null
    : remediationActive ? ((repairDelivered && !loading) ? 'RETAKE_QUIZ' : 'REPAIRING')
    : retrievalReady ? 'CHECK_UNDERSTANDING'
    : 'CONTINUE_LESSON';
  // "Keep Repairing" is the only secondary, and only beside Ready to Retake Quiz.
  const secondaryAction = primaryAction === 'RETAKE_QUIZ' ? 'KEEP_REPAIRING' : null;

  return {
    loading,
    display,
    primaryAction,
    secondaryAction,
    lessonData,
    sessionPhase,
    userMode,
    effectiveMode,
    modeOverride,
    conversationSummary,
    xp,
    level,
    lastScore,
    streamingText,
    video,
    autoVideoSignal,
    reference,
    notes,
    lastNote,
    breakVideo,
    breakVideoLoading,
    breakVideoError,
    requestBreakVideo,
    clearBreakVideo,
    currentFocus,
    signals,
    quiz,
    practice,
    handlePracticeComplete,
    engageLesson1,
    retrievalReady,
    retryState,
    retryCheckpoint,
    continueAfterCheckpointFail,
    remediationActive,
    repairDelivered,
    recheckPassed,
    requestRecheck,
    lessonComplete,
    paywalled,
    nextLesson,
    completedLesson,
    advanceToNextLesson,
    send,
    startMission,
    submitAnswer,
    handleQuizComplete,
    advanceLesson,
    endCurrentSession,
    requestMode,
    hasProfile,
  };
}
